/* eslint-disable @typescript-eslint/member-ordering */
import * as angular from 'angular';
import { Operation } from 'diffj/dist/lib/jsonPatch';
import fscreen from 'fscreen';
import * as qs from 'query-string';
import { combineLatest, from, fromEvent, Observable, partition, pipe, Subject, Subscription } from 'rxjs';
import {
  concatMap,
  filter,
  first,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { ChartFacade } from '@mode/charts/contract';
import { ChartActions, ChartSwitchActions, TempActions } from '@mode/charts/domain';
import { EncodingHelpers } from '@mode/charts/util-vegas';
import { ChartExportFacade } from '@mode/file-exports/domain';
import { SelectedReportState } from '@mode/legacy/types-app-state';
import { ofType } from '@mode/legacy/util-domain';
import {
  reportFilterActions,
  ReportFilterFacade,
  ReportViewerFacade,
  ReportViewerObservabilityService,
} from '@mode/report-viewer/domain';
import { VirtualScrollFacade } from '@mode/reports/domain';
import {
  EmbedActionType,
  EmbedBuilderActions,
  EmbedReportActions,
  EmitMessages,
  FilterPanelActions,
  RpcMessages,
} from '@mode/reports/sandbox/contract';
import { AllFlags, FeatureFlag, FeatureFlagsFacade, SortSettingsTypes } from '@mode/shared/contract-common';
import { SortSettingsModalDialogService, TimeAgoPipe } from '@mode/shared/ui';
import { ModalService } from '@mode/shared/ui-angularjs';
import { CappieReporterActions, CappieReporterService } from '@mode/shared/util-cappie-reporter';
import { Requests } from '@mode/shared/util-iframe';
import { toDictionary, MetaTags, MetaTagsService, setIFrameSrc } from '@mode/shared/util-js';
import {
  ObservabilityEvents,
  ObservabilityService,
  ViewReportObservabilityService,
} from '@mode/shared/util-observability';
import { AnalyticsEvent, AnalyticsProps, AnalyticsService, RunMethodType } from '@mode/shared/util-segment';
import { ToastService } from '@mode/shared/util-toast';
import { recordEvent } from '@mode/shared/util-tracing';
import { DataViewFacade } from '@mode/switch-views/contract';
import * as d3 from 'd3';
import { ProgressEvents } from 'libs/shared/contract-common/src/lib/cappie-reporter.types';
import { ReportFilterControl } from 'libs/shared/contract-common/src/lib/report';
import { max, min } from 'lodash';
import { ReportActionType } from '~/actions/report-actions';
import { BuildEmbedReportSelectors } from '~/selectors/embed-state/embed-state.selectors';
import { ChartExplorerService } from '~/services/chart-designer/chart-explorer.service';
import { RouterStoreService } from '~/services/router-store-service';
import { AppStore } from '~/types/main.types';
import { Actions } from '../../actions';
import { ChartViewActionType } from '../../actions/chart-commands/view.actions';
import { ChartExplorerActions, ExplorerActionType } from '../../actions/chart-designer/explorer.actions';
import { ClientActionType } from '../../actions/client.actions';
import { EditorActionType } from '../../actions/editor-actions';
import { MixableBaseController } from '../../common/component';
import { BugsnagHelpers } from '../../lib/bugsnag-helpers';
import * as reportPipes from '../../lib/report-iframe-pipes';
import { ChartViewService } from '../../services/chart-view.service';
import modalFilterPanelApplyConfirmation from '../modal-filter-panel-apply-confirmation/modal-filter-panel-apply-confirmation.html';

const FOOTER_HEIGHT = 24;
const RPC_TIMEOUT_MS = 30000;

const EMBED_MESSAGE_NAME = new Set(['autogrow', 'link', 'capture', 'getNewSize']);

/**
 * Number Deviation Clamp
 *
 * Clamp upper- or lower-bound subgroup of an
 * array based on a specified deviation threshold
 *
 * @author <franklin@modeanalytics.com>
 * @param opt
 * @returns {deviationClamp}
 */
function numberDeviationClamp(opt) {
  let numbers = [];
  const boundaryMethod = { upper: max, lower: min };
  const depth = opt.depth;
  const deviationThreshold = opt.threshold;
  const bound = boundaryMethod[opt.boundary];

  function tail(arr, num) {
    return arr.slice(num * -1);
  }

  function addNum(num) {
    return tail(numbers, depth).concat([num]);
  }

  function update(nums) {
    numbers = nums;
  }

  return function deviationClamp(num) {
    const nums = addNum(num);
    const deviation = d3.deviation(nums);

    if (deviation < deviationThreshold) {
      return bound(nums);
    } else {
      update(nums);
      return nums.slice(-1)[0];
    }
  };
}

class ViewerIFrameTimeoutError extends Error {
  constructor() {
    super('Loading viewer iframe timed out');
    Object.setPrototypeOf(this, ViewerIFrameTimeoutError.prototype);
  }
}

export class ReportViewerController extends MixableBaseController {
  static $inject = [
    '$window',
    '$element',
    '$scope',
    '$stateParams',
    'RxStoreService',
    'actions$',
    'ToastService',
    'ChartViewService',
    'ChartFacade',
    'ChartExplorerService', // This is needed for side effects related to view modal with routing refactor on
    'AnalyticsService',
    'ReportViewerFacade',
    'ReportFilterFacade',
    'DataViewFacade',
    'ModalService',
    'FeatureFlagsFacade',
    'ViewReportObservabilityService',
    'ObservabilityService',
    'RouterStoreService',
    'VirtualScrollFacade',
    'ReportViewerObservabilityService',
    'MetaTagsService',
    'ChartExportFacade',
    'CappieReporterService',
    'SortSettingsModalDialogService',
  ];

  private iframeElement: HTMLIFrameElement;
  private enabled = false;
  private embedId: string;
  private src: string;

  private frameLoading = true;
  private iframeTimeout: number;
  private iframeRetryTimer: number;

  private blockSameOrigin$: Observable<boolean>;
  private fsNoReloadEnabled$: Observable<boolean>;
  private showParamsInCaptureMode$: Observable<boolean>;
  private sandboxAttrs$: Observable<string>;
  private virtualized: boolean;

  private watchedTokensSub: Subscription | undefined;

  private chartsRendered: Set<string> = new Set();

  private heightNormalizer: (height: number) => number;
  private reportViewerReporter: ProgressEvents;
  private reportFilters: ReportFilterControl[];

  linkToPage: (args: { query: string; type: string }) => void;
  constructor(
    private $window: angular.IWindowService,
    private $element: angular.IAugmentedJQuery,
    private $scope: angular.IScope,
    private $stateParams,
    private store: AppStore,
    private actions$: Observable<Actions>,
    private alertService: ToastService,
    private chartViewService: ChartViewService, // For effects
    private chartFacade: ChartFacade,
    private explorerService: ChartExplorerService, // For effects related to view modal with routing refactor on
    private analyticsService: AnalyticsService,
    private reportViewerFacade: ReportViewerFacade,
    private reportFilterFacade: ReportFilterFacade,
    private dataViewFacade: DataViewFacade,
    private modalService: ModalService,
    private featureFlagsFacade: FeatureFlagsFacade,
    private viewReportObservability: ViewReportObservabilityService,
    private observabilityService: ObservabilityService,
    private routerStoreService: RouterStoreService,
    private virtualScrollFacade: VirtualScrollFacade,
    private reportViewerObservabilityService: ReportViewerObservabilityService,
    private metaTags: MetaTagsService,
    private chartExportFacade: ChartExportFacade,
    private cappieReporterService: CappieReporterService,
    private sortSettingsDialogService: SortSettingsModalDialogService
  ) {
    super();

    const { embed_id } = qs.parse(window.location.search);
    this.embedId = embed_id?.toString();

    this.heightNormalizer = numberDeviationClamp({
      depth: 4,
      // relative to footer height
      threshold: FOOTER_HEIGHT + 4,
      // get the higher number
      boundary: 'upper',
    });

    /**
     * Effects for triggering the honeycomb span for virtualized report view. It's here because
     * state params are easily accessible from the new Angular modules, and effect files seem to
     * have a lot of difficulty using the legacy providers.
     */
    this.virtualScrollFacade.virtualReportRendered$
      .pipe(
        filter(() => this.virtualized),
        tap(() => {
          // Fire Honeycomb event
          this.reportViewerObservabilityService.markVirtualReportRendered($stateParams['reportId']);

          // Fire Segment event
          this.analyticsService.track(AnalyticsEvent.Name.UserExperienceMilestone, {
            user_experience_id: this.analyticsService.userExperienceId,
            location: AnalyticsEvent.SubContext.ReportView,
            context: AnalyticsEvent.Context.Report,
            context_id: this.$stateParams['reportId'],
            interaction: AnalyticsEvent.Interaction.VirtualizedReportRendered,
          });
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe();

    this.virtualScrollFacade.virtualReportScrolledRendered$
      .pipe(
        filter(() => this.virtualized),
        tap(() => this.reportViewerObservabilityService.markVirtualReportScrolledRendered($stateParams['reportId'])),
        takeUntil(this.destroyed$)
      )
      .subscribe();

    this.showParamsInCaptureMode$ = combineLatest([
      this.featureFlagsFacade.asObservable(FeatureFlag.ShowReportParametersForCapture),
      this.reportViewerFacade.showParamsHeader$,
    ]).pipe(
      map(
        ([showParametersInCaptureMode, showParamsHeader]) =>
          this.isCaptureMode() && showParametersInCaptureMode && showParamsHeader
      )
    );
  }

  $onInit() {
    this.checkForRecentReportRun();
    this.reportViewerFacade.iframeLoaded(false);
    // Mark the start of the report viewer rendering
    this.observabilityService.mark(ObservabilityEvents.REPORT_RENDERING, {
      startEvent: ObservabilityEvents.LEGACY_LOAD,
      measure: true,
    });
    this.reportViewerReporter = this.cappieReporterService.progressEventAPI('report-viewer-reporter');
    this.reportViewerReporter.start('report viewer init');
    this.fsNoReloadEnabled$ = this.featureFlagsFacade.asObservable(FeatureFlag.IFrameFSNoReload);
    this.blockSameOrigin$ = this.featureFlagsFacade.asObservable(FeatureFlag.IFrameBlockSameOrigin);

    this.sandboxAttrs$ = combineLatest([
      this.blockSameOrigin$,
      this.featureFlagsFacade.asObservable(FeatureFlag.IFrameAllowForms),
      this.featureFlagsFacade.asObservable(FeatureFlag.IFrameAllowPopupEscape),
    ]).pipe(
      map(([blockSameOrigin, allowForms, allowPopupEscape]) => {
        const attrs: string[] = [];
        if (!blockSameOrigin) {
          attrs.push('allow-same-origin');
        }

        if (allowForms) {
          attrs.push('allow-forms');
        }

        if (allowPopupEscape) {
          attrs.push('allow-popups-to-escape-sandbox');
        }

        return attrs.join(' ');
      })
    );

    this.iframeElement = this.$element.find('iframe[name=mode-embed-frame]')[0] as HTMLIFrameElement;

    // May be unnecessary in the future as it's replaced by passing embed_id via postMessage
    if (this.embedId) {
      this.src += '&embed_id=' + this.embedId;
    }
    this.src += this.debugParam();

    this.reportFilterFacade.applyFilterConfirmation$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ fields, confirm }) => {
        this.openFiltersConfirmModal(fields, confirm);
      });

    this.handleToggleFilterPanelPostMessage();

    this.initFrame();

    this.watchedTokensSub = this.chartViewService.getWatchChartTokens$().subscribe({
      next(/*tokens*/) {
        // noop. dispatching requests is managed in the service
      },
    });
    this.setReportRunTime();

    this.reportFilterFacade.filterControls$?.pipe(takeUntil(this.destroyed$)).subscribe((data) => {
      this.reportFilters = data;
    });
  }
  checkForRecentReportRun() {
    combineLatest([
      this.reportViewerFacade.lastSuccessfullyRunAt$,
      this.featureFlagsFacade.asObservable(FeatureFlag.ReportRunToast),
    ])
      .pipe(take(1))
      .subscribe(([lastSuccessfullyRunAt, toastEnabled]) => {
        const timeAgoPipe = new TimeAgoPipe();
        const lastSuccessfullyRunAtTimeStamp = timeAgoPipe.transform(lastSuccessfullyRunAt);
        if (lastSuccessfullyRunAtTimeStamp === 'a few seconds ago' && toastEnabled) {
          this.reportViewerFacade.showLoadingToast();
        }
      });
  }

  $onDestroy() {
    this.watchedTokensSub?.unsubscribe();
    this.watchedTokensSub = undefined;
  }

  openFiltersConfirmModal(filters: readonly string[], confirm: () => void): void {
    const modalAttributes = {
      filters,
      confirmApply: confirm,
    };

    const modalOptions = {
      template: modalFilterPanelApplyConfirmation,
    };

    this.modalService.open(modalAttributes, modalOptions);
  }

  handleToggleFilterPanelPostMessage(): void {
    // https://mode.com/help/articles/internal-embeds/#filter-panel-toggle
    const filterPanel$ = fromEvent<MessageEvent>(this.$window, 'message').pipe(
      takeUntil(this.destroyed$),
      map((msg) => msg.data),
      filter(({ type }) => type === 'reportFilterPanelDisplay')
    );

    const [toggle$, override$] = partition(filterPanel$, (data) => data.togglePanel != null);

    toggle$.pipe(takeUntil(this.destroyed$)).subscribe(({ togglePanel }) => {
      if (typeof togglePanel !== 'boolean') return console.error(new TypeError('togglePanel should be a boolean'));
      if (togglePanel) this.store.dispatch({ type: EditorActionType.ToggleFilterPanel });
    });

    override$.pipe(takeUntil(this.destroyed$)).subscribe(({ showPanel, wleFilterPanelToggle }) => {
      // wleFilterPanelToggle is a legacy API that we're continuing to support for the time being
      // (it used to be in our public documentation.)

      if (showPanel != null && typeof showPanel !== 'boolean')
        return console.error(new TypeError('showPanel should be a boolean'));
      if (wleFilterPanelToggle != null && typeof wleFilterPanelToggle !== 'boolean')
        return console.error(new TypeError('wleFilterPanelToggle should be a boolean'));
      if (showPanel == null && wleFilterPanelToggle == null)
        return console.error(new TypeError('no arguments provided'));

      const isOpen = showPanel != null ? showPanel : wleFilterPanelToggle;
      this.store.dispatch({ type: EditorActionType.OverrideFilterPanel, isOpen });
    });
  }

  hideToast() {
    combineLatest([
      this.reportViewerFacade.activeToastId$,
      this.featureFlagsFacade.asObservable(FeatureFlag.ReportRunToast),
    ])
      .pipe(take(1))
      .subscribe(([toastId, toastEnabled]) => {
        if (toastId && toastEnabled) {
          this.reportViewerFacade.hideLoadingToast(toastId);
        }
      });
  }

  iframeLoadComplete() {
    this.hideToast();
    this.clearTimers();
    BugsnagHelpers.leaveBreadcrumbSafely('IFrame ready received');
    recordEvent('report-rendering', 'init-iframe-success');
  }

  showIFrame() {
    if (this.frameLoading) {
      this.frameLoading = false;
      this.reportViewerFacade.iframeLoaded(true);
      this.$scope.$evalAsync();
    }
  }

  hideIFrame() {
    if (!this.frameLoading) {
      this.reportViewerFacade.iframeLoaded(false);
      this.frameLoading = true;
      this.$scope.$evalAsync();
    }
  }

  initFrame() {
    // This is a complex set of observables that went through a lot of development and testing. Do not
    // change without consulting the author.
    combineLatest(
      from(import(/* webpackChunkName: "iframe-transport" */ '@mode/reports/sandbox/util-transport')),
      this.blockSameOrigin$,
      this.fsNoReloadEnabled$
    )
      .pipe(first())
      .subscribe(([imports, blockSameOrigin, noReload]) => {
        const { Rpc, Emit } = imports;

        // This ticks if we're in full screen mode and there's a new report to load
        const fullScreenReload$ = this.actions$.pipe(
          ofType(ClientActionType.LoadReportSuccess),
          withLatestFrom(this.store.select('transient', 'window', 'isFullscreen')),
          filter(([, isFullscreen]) => isFullscreen),
          map(([, isFullScreen]) => isFullScreen),
          takeUntil(this.destroyed$),
          tap(() => this.hideIFrame())
        );

        /** We want to show the iframe after the first patch is sent, so there's something
         * nice for the user to look at on first page load, instead of just the raw layout.
         * This variable is used to trigger showing it on patch updates.
         */
        let showIFrameNextPatch = false;

        // Setup a queue to send the RPC messages. It's a single queue
        // to make sure any remaining messages can be flushed in the event
        // of a full-screen refresh.
        const rpcQueue$ = new Subject<Requests>();
        rpcQueue$
          .pipe(
            takeWhile(() => this.iframeElement.contentWindow != null),
            // Use concatMap to avoid processing patches in the wrong order
            concatMap(
              (msg) =>
                Rpc.sendRequest(this.$window, this.iframeElement.contentWindow, msg).catch((err) => {
                  BugsnagHelpers.notifySafely(err, (event) => {
                    event.groupingHash = 'ReportViewerMsgQueueRpcTimeout';

                    event.addMetadata('debugInfo', {
                      messageType: msg.type,
                      blockSameOrigin,
                      noReload,
                      iframeMounted: this.$window.document.body.contains(this.iframeElement),
                      ...(msg.type === RpcMessages.MessageTypes.UpdateState ? { patchCount: msg.diff.length } : {}),
                    });
                  });
                }),
              (msg) => msg
            ),
            tap((msg) => {
              // When initreport is sent, queue up to show iframe on next patch
              if (msg.type === RpcMessages.MessageTypes.InitReport) {
                recordEvent('report-rendering', 'init-report');
                showIFrameNextPatch = true;
              } else if (msg.type === RpcMessages.MessageTypes.UpdateState && showIFrameNextPatch) {
                showIFrameNextPatch = false;
                this.showIFrame();
                this.reportViewerReporter.end('report viewer iframe loaded');
              }
            }),
            takeUntil(this.destroyed$)
          )
          .subscribe();

        /**
         * Setup producer/consumer pattern for generating and sending patches.
         * Using a separate observable for this allows for explicit control of when
         * to reset the queue, which is really important when a fullscreen refresh
         * is triggered.
         */
        let subscription: Subscription;
        const diffProducer$ = new Subject<Operation[]>();
        const diffConsumer$ = diffProducer$.pipe(reportPipes.bufferPatches());

        // Send the init report request, which sends the html and theme so the iframe is ready to display the visualizations
        const sendInit = pipe(
          tap(() => {
            // This resets the patch queue
            if (subscription != null) {
              subscription.unsubscribe();
            }
            subscription = diffConsumer$.subscribe((delta) => rpcQueue$.next(new RpcMessages.UpdateState(delta)));
          }),
          switchMap(({ layout, theme }) =>
            this.featureFlagsFacade.allFlags$.pipe(
              first(),
              map((flags) => ({ layout, theme, flags }))
            )
          ),
          tap(({ layout, theme, flags }: { layout: string; theme: SelectedReportState['theme']; flags: AllFlags }) => {
            this.observabilityService.mark(ObservabilityEvents.REPORT_LAYOUT, {
              startEvent: ObservabilityEvents.REPORT_RENDERING,
              measure: true,
            });
            this.reportViewerObservabilityService.markReportCaptureRendering();
            rpcQueue$.next(
              new RpcMessages.InitReport(
                layout,
                theme.href,
                theme.customSource,
                flags,
                this.isCaptureMode(),
                this.embedId
              )
            );
          })
        );

        const getLayout = reportPipes.getLayout(
          this.reportViewerFacade.reportLayoutNoEmpty$,
          this.reportViewerFacade.themeNoEmpty$
        );

        if (noReload) {
          Emit.getMessageStream(this.$window)
            .pipe(
              reportPipes.waitReady(),
              tap(() => this.iframeLoadComplete()),
              getLayout,
              // Switchmap into fullscreen refresh stream to allow tick from here
              switchMap(
                () => fullScreenReload$.pipe(startWith(false)),
                (layout) => layout
              ),
              sendInit,
              reportPipes.generatePatch(this.store.select(BuildEmbedReportSelectors.embedState)),
              reportPipes.reportError(this.src),
              takeUntil(this.destroyed$)
            )
            .subscribe((delta) => {
              diffProducer$.next(delta);
            });
        } else {
          // Need to reset the iframe when an appropriate action is fired
          fullScreenReload$.subscribe(() => {
            this.startIFrameLoad(this.src);
          });

          // Has multiple pipes because the pipe function only maintains types for up to 9 parameters
          Emit.getMessageStream(this.$window)
            .pipe(
              reportPipes.waitReady(),
              tap(() => this.iframeLoadComplete()),
              getLayout,
              sendInit,
              reportPipes.generatePatch(this.store.select(BuildEmbedReportSelectors.embedState)),
              reportPipes.reportError(this.src),
              takeUntil(this.destroyed$)
            )
            .subscribe((delta) => {
              diffProducer$.next(delta);
            });
        }

        Emit.getMessageStream(this.$window)
          .pipe(
            filter((msg) => msg.type === EmitMessages.MessageTypes.Action),
            map((msg) => (msg.type === EmitMessages.MessageTypes.Action ? msg : null)),
            takeUntil(this.destroyed$)
          )
          .subscribe((msg) => {
            this.checkActionBlockers(msg.action).then((blocked) => {
              if (!blocked) {
                const action = this.convertViewerAction(msg.action);
                if (action != null) {
                  this.$scope.$applyAsync(() => this.store.dispatch(action));
                } else {
                  this.executeAction(msg.action);
                }
              }
            });
          });

        this.startIFrameLoad(this.src);
        BugsnagHelpers.leaveBreadcrumbSafely('Initial Frame Load', { url: this.src });
      });

    // Support for existing embed messages
    fromEvent<MessageEvent>(this.$window, 'message')
      .pipe(
        takeUntil(this.destroyed$),
        filter((message) => message && message.data && message.data.name && EMBED_MESSAGE_NAME.has(message.data.name))
      )
      .subscribe((ev) => {
        const msg = ev.data;
        switch (msg.name) {
          case 'link':
            this.linkToPage({ query: msg.query, type: msg.type });
            break;
        }
      });
  }

  checkActionBlockers(action: EmbedBuilderActions.Actions): PromiseLike<boolean> {
    return new Promise((resolve, reject) => {
      switch (action.type) {
        case EmbedReportActions.Type.UpdateSortSettings:
          this.sortSettingsDialogService.open({
            encoding: action.event.encoding,
            selectedColumn: action.event.selectedColumn,
            descending: action.event.descending,
            chartTitle: action.event.chartTitle,
            applyAction: (sorts: SortSettingsTypes.Sort[]) => {
              action.event.encResult = sorts.reduce(
                (encResult, sort) =>
                  EncodingHelpers.addTableSort(encResult.encoding, sort.selected.value, sort.descending, false),
                EncodingHelpers.clearSorts(action.event.encoding)
              );
              resolve(false);
            },
            cancelAction: () => {
              resolve(true);
            },
          });
          break;
        default:
          resolve(false);
          break;
      }
    });
  }

  startIFrameLoad(url: string) {
    this.clearTimers();
    recordEvent('report-rendering', 'init-iframe', { iframeUrl: url });
    setIFrameSrc(this.iframeElement, url);

    // attempt retry once if necessary
    this.iframeRetryTimer = this.$window.setTimeout(() => {
      recordEvent('report-rendering', 'init-iframe-retry');
      setIFrameSrc(this.iframeElement, url);
      BugsnagHelpers.leaveBreadcrumbSafely('Retry Frame Load', { url });
    }, RPC_TIMEOUT_MS / 3.0);

    // Log an error if the iframe never responds
    this.iframeTimeout = this.$window.setTimeout(() => {
      recordEvent('report-rendering', 'init-iframe-failed');
      BugsnagHelpers.notifySafely(new ViewerIFrameTimeoutError(), (event) => {
        event.groupingHash = 'ViewerIFrameTimeoutError';

        event.addMetadata('iframe', {
          src: this.src,
          embed_id: this.embedId,
          fullscreen: fscreen.fullscreenElement != null,
        });
      });
    }, RPC_TIMEOUT_MS);
  }

  clearTimers() {
    this.$window.clearTimeout(this.iframeTimeout);
    this.$window.clearTimeout(this.iframeRetryTimer);
  }

  // To simplify conversion from embed-frame. Ideally, these would all be done via actions instead.
  executeAction(action: EmbedBuilderActions.Actions) {
    // don't digest if we're just passing on a document click, as ui-bootstrap can trigger digests
    if (action.type === EmbedReportActions.Type.DocumentClick) {
      this.$element.click();
    } else {
      switch (action.type) {
        case EmbedActionType.ShowAlert:
          if (action.success) {
            this.alertService.success(action.message);
          } else {
            this.alertService.error(action.message);
          }
          break;
        case EmbedActionType.CopyChart:
          this.chartViewService.dispatchCopyChart(action.token);
          break;
        case EmbedReportActions.Type.Autogrow:
          if (this.embedId != null) {
            let height = this.heightNormalizer(action.height);

            // since the embed.js listening auto adds the 24px footer we
            // subtract that from the height if we aren't showing the footer
            height -= this.$element.hasClass('without-footer') ? FOOTER_HEIGHT : 0;

            // since we're inside an JS embed iframe
            window.parent.postMessage({ height: height, id: this.embedId }, '*');
          } else {
            console.warn('Embed id not found');
          }
          break;
        case EmbedActionType.Back:
          this.chartViewService.undo(action.chartToken);
          break;
        case EmbedActionType.Forward:
          this.chartViewService.redo(action.chartToken);
          break;
        case EmbedActionType.MuzeChartRendered:
          break;
        case EmbedReportActions.Type.ReportStartRender:
          const reportToken = this.$stateParams['reportId'];
          this.viewReportObservability.startTrace(reportToken);
          this.virtualScrollFacade.viewerSetVirtualEnabled(action.virtualized);

          this.virtualized = action.virtualized;
          break;
        case EmbedReportActions.Type.UpdateVizIntersections:
          this.virtualScrollFacade.viewerSetInViewport(action.intersections);
          break;
        case EmbedReportActions.Type.ReportHasScrolled:
          //set to scroll, used for virtual scrolling reports
          this.virtualScrollFacade.viewerSetScrolled(true);
          break;
        case EmbedReportActions.Type.ReportRendered:
          //this action only happens when not a virtual scroll report
          if (!this.virtualized) {
            this.reportViewerObservabilityService.markReportRendered(
              this.$stateParams['reportId'],
              this.isCaptureMode()
            );
            this.analyticsService.endTiming(AnalyticsEvent.Name.LoadingReport);
            this.viewReportObservability.endTrace();
            recordEvent('report-rendering', 'report-rendered');
          }
          break;

        case EmbedReportActions.Type.ExportChartCsv:
          this.chartExportFacade.exportReportChartCsv(action.token);
          this.chartExportFacade.openDrawer();
          break;
        case EmbedActionType.AnalyticsEvent:
          this.analyticsService.track(action.name, {
            location: action.location,
            action: action.action,
            context: action.context,
            context_id: action.context_id,
            chartType: action.chartType,
          });
          break;
        case EmbedReportActions.Type.ViewUnaggregatedData:
          this.chartFacade.viewUnaggregatedChartData(action.chartToken);
          break;
      }

      this.$scope.$applyAsync();
    }
  }

  private trackExploreInteraction(
    event: AnalyticsEvent.Name,
    props: AnalyticsProps
  ): ChartExplorerActions.TrackExploreInteraction {
    return {
      type: ExplorerActionType.TrackExploreInteraction,
      event,
      props,
    };
  }

  private isCaptureMode() {
    return !!this.metaTags.getTag(MetaTags.CaptureMode);
  }

  private debugParam() {
    const debugjs = this.metaTags.getTag(MetaTags.DebugJS);
    return debugjs ? `&${MetaTags.DebugJS}=${debugjs}` : '';
  }

  private setReportRunTime() {
    this.reportViewerFacade.reportRunCompletedAt$.subscribe((completedTime) => {
      this.store.dispatch({ type: ReportActionType.SetReportCaptureRunCreatedTime, createdAt: completedTime });
    });
  }

  // This may be moved to another file for improved organization
  convertViewerAction(action: EmbedBuilderActions.Actions): Actions {
    switch (action.type) {
      // Report Filters
      case FilterPanelActions.Type.ApplyFilters:
        recordEvent('report-viewer', 'filter-apply', { filters: action.filters });
        return reportFilterActions.requestApplyReportFilters({
          selected: toDictionary(action.filters, (f) => f.selected),
        });
      case FilterPanelActions.Type.ResetFilters:
        recordEvent('report-viewer', 'filter-reset-all', { filters: action.filters });
        return reportFilterActions.resetReportFilters({
          defaults: toDictionary(action.filters, (f) => f.default),
        });
      case FilterPanelActions.Type.ResetFilter:
        recordEvent('report-viewer', 'filter-reset', { filters: action.filter });
        return reportFilterActions.resetReportFilter({
          token: action.filter.token,
          defaults: action.filter.default,
        });
      case FilterPanelActions.Type.FilterSelectionChange:
        recordEvent('report-viewer', 'filter-selection-change', { token: action.token });
        return reportFilterActions.filterSelectionChange({
          token: action.token,
          selection: action.selection,
        });
      case FilterPanelActions.Type.FilterPanelLoaded:
        this.analyticsService.track(AnalyticsEvent.Name.UserExperienceMilestone, {
          user_experience_id: this.analyticsService.userExperienceId,
          location: AnalyticsEvent.SubContext.ReportView,
          context: AnalyticsEvent.Context.Report,
          context_id: this.$stateParams['reportId'],
          interaction: AnalyticsEvent.Interaction.Filter,
        });
        return reportFilterActions.filterPanelLoaded();

      // Chart lightbox interactions
      case EmbedActionType.ExploreChart:
        return {
          type: ExplorerActionType.ExploreChart,
          chartToken: action.chartToken,
          props: action.props,
        };

      case EmbedActionType.ExpandKebab:
        this.analyticsService.run(RunMethodType.Track, AnalyticsEvent.Name.ExploreClickKebab, action.props);

        return this.trackExploreInteraction(AnalyticsEvent.Name.ExploreClickKebab, action.props);

      case EmbedActionType.ClickChartMark:
        return this.trackExploreInteraction(AnalyticsEvent.Name.ClickChartDataSeries, action.props);

      case EmbedActionType.ClickDrilldownInterval:
        return this.trackExploreInteraction(AnalyticsEvent.Name.ClickDrilldownInterval, action.props);

      case EmbedActionType.ClickViewData:
        return this.trackExploreInteraction(AnalyticsEvent.Name.ClickViewData, action.props);

      case EmbedActionType.ClickExploreFromHere:
        return this.trackExploreInteraction(AnalyticsEvent.Name.ClickExploreFromHere, action.props);

      // Chart view interactions
      case EmbedActionType.ChangeHierarchy:
        if (action.forward) {
          this.analyticsService.run(RunMethodType.Track, AnalyticsEvent.Name.ExploreIncreaseGranularity, action.props);
        } else {
          this.analyticsService.run(RunMethodType.Track, AnalyticsEvent.Name.ExploreDecreaseGranularity, action.props);
        }

        return { ...action, type: ChartViewActionType.ChangeHierarchy };

      case EmbedActionType.DrillDown:
        return { ...action, type: ChartViewActionType.DrillDown };

      case EmbedActionType.DynamicDrillDown:
        return { ...action, type: ChartViewActionType.DynamicDrillDown };

      case EmbedActionType.ExploreChartMark:
        return { ...action, type: TempActions.Type.ExploreChartMark };

      case EmbedActionType.ViewData:
        return { ...action, type: TempActions.Type.ExploreChartWithData };

      case EmbedActionType.ChartRendered:
      case EmbedActionType.NotebookVisualizationRendered:
        if (!this.chartsRendered.has(action.token)) {
          recordEvent('report-rendering', 'viz-rendered', {
            token: action.token,
            type: action.chartType,
            timestamp: action.timestamp,
            rendered: action.visualizationRendered,
            duration: action.duration,
          });

          this.chartsRendered.add(action.token);
          if (action.duration) {
            const now = performance.now();

            // Note that this can fail due to the browser not supporting the options blob
            this.observabilityService.measure(ObservabilityEvents.REPORT_VIZ_RENDER, {
              start: now - action.duration,
              end: now,
              detail: {
                options: {
                  chartToken: action.token,
                  chartType: action.chartType,
                  ...action.options,
                },
              },
            });
          }

          this.analyticsService.track(AnalyticsEvent.Name.UserExperienceMilestone, {
            user_experience_id: this.analyticsService.userExperienceId,
            location: AnalyticsEvent.SubContext.ReportView,
            context: AnalyticsEvent.Context.Report,
            context_id: this.$stateParams['reportId'],
            interaction:
              action.type === EmbedActionType.NotebookVisualizationRendered
                ? AnalyticsEvent.Interaction.NotebookOutput
                : AnalyticsEvent.Interaction.Visualization,
            interaction_id: action.token,
            rendered_successfully: action.visualizationRendered,
          });
        }

        //only set virtual scroll rendered if we are using virtualized
        if (this.virtualized) {
          // If rendering "failed", we'll still count it as rendered as far as virtual scroll is concerned
          this.virtualScrollFacade.viewerSetRendered(action.token, true);
        }
        return ChartActions.reportViewerChartRendered();

      // Flamingo
      case EmbedReportActions.Type.SetTableOffset:
        return ChartActions.setOffset({
          token: action.token,
          offset: action.offset,
        });

      case EmbedReportActions.Type.SetTableColumnSearch:
        return ChartActions.setTableColumnSearch({
          token: action.token,
          columnSearchModel: action.columnSearchModel,
        });

      case EmbedReportActions.Type.UpdateEncoding:
        return {
          type: ChartViewActionType.UpdateEncoding,
          chartToken: action.token,
          result: action.encResult,
          pillAction: action.pillAction,
        };

      case EmbedReportActions.Type.ProgressReporting:
        return CappieReporterActions.progressReporting({ eventData: action.eventData });
      case EmbedReportActions.Type.GetDataProfiling:
        return ChartSwitchActions.getDataProfiling({
          token: action.token,
          cols: action.cols,
          reportFilters: this.reportFilters,
          referenceId: action.referenceId,
        });
        break;
      case EmbedReportActions.Type.UpdateSortSettings:
        return {
          type: ChartViewActionType.UpdateEncoding,
          chartToken: action.token,
          result: action.event.encResult,
        };
    }

    return null;
  }
}

export const ReportViewerComponent: angular.IComponentOptions = {
  bindings: {
    view: '<',
    src: '<',
    linkToPage: '&',
  },
  template: `
  <mode-report-viewer-file-exports></mode-report-viewer-file-exports>
  <div class="report-viewer-full-height" ng-class="{'report-view-container': ctrl.isCaptureMode()}">
    <report-params-container
      ng-if="ctrl.showParamsInCaptureMode$ | async:this"
      show-params-header="ctrl.reportViewerFacade.showParamsHeader$ | async:this"
      is-param-container-visible="true"
    ></report-params-container>
    <iframe
      name="mode-embed-frame"
      class="embed-iframe"
      ng-class="{invisible: ctrl.frameLoading}"
      height="100%"
      frameborder="0"
      ng-attr-sandbox="allow-scripts allow-popups allow-top-navigation allow-downloads {{ (ctrl.sandboxAttrs$ | async:this) }}">
    </iframe>
  </div>
  `,
  controller: ReportViewerController,
  controllerAs: 'ctrl',
};
